import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';

import 'grapesjs/dist/css/grapes.min.css';
import grapesJS from 'grapesjs';
import pluginMJML from 'grapesjs-mjml';
import pluginScribeJs from 'grapesjs-plugin-scribejs';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { ValidationException } from '../../exceptions/validation-exception';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SpinnerDialogComponent } from '../dialogs/spinner-dialog/spinner-dialog.component';
import { SpinnerService } from 'src/app/services/spinner.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UpdateNewsletterTemplateComponentStore } from 'src/app/stores/components/newsletter-template-update/update-newsletter-template.component-store';
import { UpdateNewsletterTemplateModel } from 'src/app/models/update-newsletter-template-model';
import { NewsletterTemplateDetailComponentStore } from 'src/app/stores/components/newsletter-template-detail/newsletter-template-detail.component-store';
import { NewsletterTemplateModel } from 'src/app/models/newsletter-template-model';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ResponsiveBaseComponent } from '../responsive-base/responsive-base.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';
import { DeleteNewsletterTemplateComponentStore } from 'src/app/stores/components/newsletter-template-delete/delete-newsletter-template.component-store';

declare const grapesjs: any;

@Component({
  selector: 'app-newsletter-template-edit',
  templateUrl: './newsletter-template-edit.component.html',
  styleUrl: './newsletter-template-edit.component.scss',
  standalone: false,
})
export class NewsletterTemplateEditComponent extends ResponsiveBaseComponent implements OnInit {
  public readonly minNameLength: number = 1;
  public readonly maxNameLength: number = 32;
  public newsletterTemplateEditFormGroup: FormGroup;

  @ViewChild('gjs', { static: true })
  private gjs: ElementRef;
  private editor: any;

  private readonly ngUnsubscribeState = new Subject<void>();
  private loadingSpinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;

  private companyId: string;
  private newsletterTemplateId: string;

  constructor(
    responsive: BreakpointObserver,
    private readonly newsletterTemplateDetailComponentStore: NewsletterTemplateDetailComponentStore,
    private readonly updateNewsletterTemplateComponentStore: UpdateNewsletterTemplateComponentStore,
    private readonly deleteNewsletterTemplateComponentStore: DeleteNewsletterTemplateComponentStore,
    private readonly spinnerService: SpinnerService,
    private readonly snackBar: MatSnackBar,
    private readonly confirmationDialog: MatDialog,
    private readonly router: Router,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
  ) {
    super(responsive);
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.companyId = params.companyId;
      this.newsletterTemplateId = params.newsletterTemplateId;
    });

    this.newsletterTemplateDetailComponentStore.getNewsletterTemplate({ companyId: this.companyId, newsletterTemplateId: this.newsletterTemplateId });

    this.newsletterTemplateDetailComponentStore.newsletterTemplate$.pipe(takeUntil(this.ngUnsubscribeState), filter(r => Boolean(r))).subscribe((newsletterTemplate) => {
      this.initializeEditor(newsletterTemplate);
      this.newsletterTemplateEditFormGroup.patchValue({ 'name': newsletterTemplate.name });
    });

    this.updateNewsletterTemplateComponentStore.errorMessage$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((errorMessage) => {
      this.hideSpinner(true);
      if (errorMessage !== null && errorMessage !== undefined) {
        if (errorMessage instanceof ValidationException) {
          const validationViolationMessage = errorMessage.violations.map(v => v.field + ' ' + v.message).join("\n");
          this.snackBar.open(validationViolationMessage, "Dismiss");
        }
        else {
          this.snackBar.open(errorMessage, "Dismiss");
        }
      }
    });

    this.updateNewsletterTemplateComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });

    this.deleteNewsletterTemplateComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });

    this.updateNewsletterTemplateComponentStore.success$.pipe(takeUntil(this.ngUnsubscribeState), filter(success => Boolean(success))).subscribe(() => {
      this.router.navigate(['/companies/', this.companyId, 'newsletters', 'templates']);
      this.snackBar.open("Newsletter Template updated successfully", "Dismiss");
    });

    this.deleteNewsletterTemplateComponentStore.success$.pipe(takeUntil(this.ngUnsubscribeState), filter(success => Boolean(success))).subscribe(() => {
      this.router.navigate(['/companies/', this.companyId, 'newsletters', 'templates']);
      this.snackBar.open("Newsletter Template deleted successfully", "Dismiss");
    });

    this.newsletterTemplateEditFormGroup = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(this.minNameLength), Validators.maxLength(this.maxNameLength)]],
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribeState.next();
    this.ngUnsubscribeState.complete();
    this.clearEditor();
  }

  initializeEditor(newsletterTemplate: NewsletterTemplateModel): void {
    this.gjs.nativeElement.innerHTML = '<mjml><mj-body><mj-section><mj-column></mj-column></mj-section></mj-body></mjml>';
    this.editor = grapesJS.init({
      container: '#gjs',
      height: '70%',

      fromElement: true,
      plugins: [
        pluginScribeJs,
        pluginMJML,
        'grapesjs-plugin-borders',
      ],
      pluginsOpts: {
        'grapesjs-plugin-borders': {
          'main_color': '#d96caa', // For all
          // OR
          // 'selected': '#2ca4e0',
          // 'resizer': '#FF0000',
          // 'toolbar': '#FF0000',
          // 'badge': '#DD0000',
          // 'highlighter': '#FF0000'
        }
      },
      style: '.txt-red{color: #d96caa}',
      storageManager: false,
    });

    this.editor.loadProjectData(newsletterTemplate.projectData);

    this.addSaveButton();
    this.addDeleteButton();
    this.saveNewsletterTemplate();
    this.deleteNewsletterTemplate();
  }

  clearEditor() : void {
    this.editor.destroy();
  }

  addSaveButton(): void {
    this.editor.Panels.addButton('options', {
      id: ' save-button',
      className: 'fa fa-floppy-o',
      command: 'save-newsletter-template',
      attributes: { title: 'Save Newsletter Template' }
    });
  }

  addDeleteButton(): void {
    this.editor.Panels.addButton('options', {
      id: ' delete-button',
      className: 'fa fa-trash-o',
      command: 'delete-newsletter-template',
      attributes: { title: 'Delete Newsletter Template' }
    });
  }

  saveNewsletterTemplate(): void {
    this.editor.Commands.add('save-newsletter-template', {
      run: (editor: any) => {
        this.newsletterTemplateEditFormGroup.markAllAsTouched();
        if (this.newsletterTemplateEditFormGroup.valid) {

          const templateContent = editor.runCommand('mjml-code-to-html')?.html;
          const htmlContent = editor.getHtml();
          const cssContent = editor.getCss();
          const projectData = editor.getProjectData();

          this.showSpinner();
          const updateNewsletterTemplate = new UpdateNewsletterTemplateModel();
          updateNewsletterTemplate.newsletterTemplateId = this.newsletterTemplateId;
          updateNewsletterTemplate.name = this.newsletterTemplateEditFormGroup.value.name;
          updateNewsletterTemplate.htmlContent = htmlContent;
          updateNewsletterTemplate.cssContent = cssContent;
          updateNewsletterTemplate.templateContent = templateContent;
          updateNewsletterTemplate.projectData = projectData;
          this.updateNewsletterTemplateComponentStore.updateNewsletterTemplate({ companyId: this.companyId, updateNewsletterTemplate: updateNewsletterTemplate });
        }
      }
    });
  }

  deleteNewsletterTemplate(): void {
    this.editor.Commands.add('delete-newsletter-template', {
      run: (editor: any) => {
        const dialogRef = this.confirmationDialog.open(ConfirmationDialogComponent, {
          data: {
            title: "Are you sure?",
            message: "This can't be undone.\n",
            data: "",
            confirm: "Delete"
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.showSpinner();
            this.deleteNewsletterTemplateComponentStore.deleteNewsletterTemplate({ companyId: this.companyId, newsletterTemplateId: this.newsletterTemplateId });
          }
        });
      }
    });
  }

  private showSpinner() {
    this.loadingSpinnerDialogRef = this.spinnerService.show();
  }

  private hideSpinner(loaded: boolean) {
    if (loaded && this.loadingSpinnerDialogRef !== null) {
      this.spinnerService.hide(this.loadingSpinnerDialogRef);
      this.loadingSpinnerDialogRef = null;
    }
  }
}